import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography, Autocomplete, TextField, Divider, AlertTitle } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-input-2';
import { AddVenueSchema } from './AddVenueSchema';
import * as VenuesActions from '../../../../../core/venues/action';
import * as LocationActions from '../../../../../core/location/action';
import {
  FeedbackError,
  Input,
  Button,
  TranslucentContainer,
  Textarea,
  Dropdown,
  Message,
  DropzoneFile,
  AlertComponent,
} from '../../../../../components';
import { VenueFeaturesCheck, ContactPoint, SocialNetworks, PicturesVenue } from '..';
import 'react-phone-input-2/lib/style.css';
import './AddVenue.scss';
import { useIsMobile } from '../../../../../hooks/useIsMobile';
import { useScrollToFirstError } from '../../../../../hooks/useScrollToFirstError';

function MobileDivider() {
  const isMobile = useIsMobile();

  return isMobile ? (
    <Grid item xs={12} sm={10} mt={3} mb={1}>
      <Divider />
    </Grid>
  ) : null;
}

function VenueAlert(venueName) {
  return (
    <AlertComponent
      title="Manage Venues"
      content={
        <p>
          You have already registered venue <strong>{venueName}</strong>. If you need to create a
          new one, please create a new user.
        </p>
      }
      severity="warning"
    />
  );
}

function FormContentVenue({ onSaveVenue }) {
  const initialValues = {
    name: '',
    contact_name: '',
    contact_email: '',
    contact_phone_number: '',
    business_address: '',
    zip: '',
    facebook: '',
    instagram: '',
    twitter: '',
    website: '',
    business_type: '',
    business_number: '',
    business_EIN: '',
    description: '',
    fk_state: undefined,
    fk_city: undefined,
  };
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const justifyContentValue = isMobile ? 'start' : 'center';
  const spacingValue = (notMobileValue) => (isMobile ? 0 : notMobileValue);

  const [featureList, setFeatureList] = useState([]);
  const [picturesList, setPicturesList] = useState([]);

  const {
    venues: { venuePictures, features, currentVenuePictures, success, error, currentVenue },
    location: { states, cities },
  } = useSelector((store) => store);

  const setSelectedState = useCallback(
    (stateSelected) => {
      const { id } = stateSelected;
      dispatch(LocationActions.getCitiesByStateID(id));
    },
    [dispatch],
  );
  useEffect(() => {
    if (currentVenue?.social_network?.length > 0) {
      currentVenue.facebook = currentVenue.social_network.find((s) => s.name === 'Facebook')?.url;
      currentVenue.instagram = currentVenue.social_network.find((s) => s.name === 'Instagram')?.url;
      currentVenue.twitter = currentVenue.social_network.find((s) => s.name === 'Twitter')?.url;
    }
    if (features?.length > 0) setFeatureList([...features]);
    if (currentVenuePictures?.length > 0) setPicturesList([...currentVenuePictures]);
    dispatch(LocationActions.getStates());
    return () => {
      if (currentVenue != null) setSelectedState({ id: currentVenue.fk_state });
    };
  }, [dispatch, currentVenue, features, currentVenuePictures, setSelectedState]);

  const onChangeFiles = (incomingFiles) => {
    dispatch(VenuesActions.addImagesToVenue(incomingFiles));
  };

  const onChangeStatusFeatureList = (e) => {
    const { checked, id } = e.target;
    if (!id) return;
    const result = featureList.map((i) => (i.id === Number(id) ? { ...i, state: checked } : i));
    setFeatureList(result);
  };

  const canManageVenue = () => currentVenue !== null;
  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    const featureActivated = [];
    featureList.forEach((i) => (i.state === true ? featureActivated.push(i.id) : null));
    // The server stores the EIN as 9 digits; accept 12-3456789 in the form and strip the dash here.
    const cleanValues = {
      ...values,
      business_EIN: String(values.business_EIN || '').replace(/\D/g, ''),
    };
    onSaveVenue(cleanValues, featureActivated, canManageVenue());
    setSubmitting(true);
  };
  const handleImageSelect = (bannerKey, setFieldValue) => {
    currentVenue.banner = bannerKey;
    // Also update the form values so it's included in the submit payload
    setFieldValue('banner', bannerKey);
  };

  const handleDeleteImage = async ({ key }, setFieldValue) => {
    const isCurrentBanner = currentVenue?.banner === key;

    // Update local state immediately to reflect deletion
    const updatedPictures = picturesList.filter((pic) => pic.key !== key);
    setPicturesList(updatedPictures);

    dispatch(VenuesActions.deleteVenuePicture(key));

    // If deleting the current banner, auto-select next image or clear banner
    if (isCurrentBanner) {
      if (updatedPictures.length > 0) {
        // Auto-select the first remaining image
        const nextBannerKey = updatedPictures[0].key;
        currentVenue.banner = nextBannerKey;
        setFieldValue('banner', nextBannerKey);
      } else {
        // No images left, clear the banner
        currentVenue.banner = null;
        setFieldValue('banner', null);
      }
    }
  };

  return (
    <Formik
      initialValues={canManageVenue() ? currentVenue : initialValues}
      validationSchema={AddVenueSchema}
      onSubmit={onSubmit}>
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        onFocus,
        submitCount,
        isSubmitting,
      }) => (
        <AddVenueFormBody
          values={values}
          touched={touched}
          errors={errors}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSubmit={handleSubmit}
          setFieldValue={setFieldValue}
          onFocus={onFocus}
          submitCount={submitCount}
          isSubmitting={isSubmitting}
          states={states}
          cities={cities}
          setSelectedState={setSelectedState}
          justifyContentValue={justifyContentValue}
          spacingValue={spacingValue}
          canManageVenue={canManageVenue}
          picturesList={picturesList}
          handleImageSelect={handleImageSelect}
          handleDeleteImage={handleDeleteImage}
          currentVenue={currentVenue}
          venuePictures={venuePictures}
          onChangeFiles={onChangeFiles}
          featureList={featureList}
          onChangeStatusFeatureList={onChangeStatusFeatureList}
        />
      )}
    </Formik>
  );
}

function AddVenueFormBody({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  onFocus,
  submitCount,
  isSubmitting,
  states,
  cities,
  setSelectedState,
  justifyContentValue,
  spacingValue,
  canManageVenue,
  picturesList,
  handleImageSelect,
  handleDeleteImage,
  currentVenue,
  venuePictures,
  onChangeFiles,
  featureList,
  onChangeStatusFeatureList,
}) {
  const errorCount = Object.keys(errors).length;
  const selectedState = states.find((state) => state.id === values.fk_state);
  const selectedCity = cities.find((city) => city.id === values.fk_city);

  useScrollToFirstError({ submitCount, errors, isSubmitting });

  return (
    <Form onSubmit={handleSubmit}>
      <Grid
        container
        justifyContent="space-around"
        paddingX={spacingValue(15)}
        marginTop={spacingValue(7)}>
        <Grid item xs={12} sm={10} marginY={1}>
          <Input
            placeholder="Venue Name"
            name="name"
            type="text"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.name && touched.name && 'error'}
          />
          <FeedbackError name="name" errors={errors} touched={touched} />
        </Grid>
        <Grid item xs={12} sm={10} marginBottom={1}>
          <Input
            placeholder="Street Address"
            name="business_address"
            type="text"
            value={values.business_address}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.business_address && touched.business_address && 'error'}
          />
          <FeedbackError name="business_address" errors={errors} touched={touched} />
        </Grid>
        <Grid item xs={12} sm={10} marginBottom={1}>
          <Grid container justifyContent="space-between">
            <Grid item xs={12} sm={6} marginBottom={1}>
              {states.length > 0 ? (
                <Autocomplete
                  id="fk_state"
                  name="fk_state"
                  classes={{ label: 'my-class-name' }}
                  placeholder="State"
                  options={states}
                  disablePortal
                  value={selectedState}
                  onChange={(e, value) => {
                    setFieldValue('fk_state', value.id);
                    setFieldValue('fk_city', undefined);
                    setSelectedState(value);
                  }}
                  getOptionLabel={(option) => `${option.state_code} - ${option.name}`}
                  renderInput={(params) => <TextField {...params} placeholder="State" />}
                  clearOnEscape
                  className={errors.fk_state && touched.fk_state && 'error'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '50px',
                      background: '#ffffff',
                      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                      height: '50px',
                      paddingLeft: '2rem',
                    },
                  }}
                />
              ) : null}

              <FeedbackError name="fk_state" errors={errors} touched={touched} />
            </Grid>
            <Grid item mr={spacingValue(-5.5)} xs={12} sm={6} marginBottom={1}>
              <Autocomplete
                id="fk_city"
                name="fk_city"
                disableClearable
                disabled={cities.length === 0}
                options={cities || []}
                getOptionLabel={(option) => option.name}
                value={selectedCity}
                disablePortal
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={cities.length === 0 ? 'Select a state first' : 'City'}
                  />
                )}
                onChange={(e, value) => {
                  setFieldValue('fk_city', value.id);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '50px',
                    background: '#ffffff',
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                    height: '50px',
                  },
                }}
              />
              <FeedbackError name="fk_city" errors={errors} touched={touched} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={10} marginBottom={1}>
          <Input
            placeholder="ZIP Code"
            name="zip"
            type="text"
            value={values.zip}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.zip && touched.zip && 'error'}
          />
          <FeedbackError name="zip" errors={errors} touched={touched} />
        </Grid>

        <MobileDivider />

        <Grid item xs={12} sm={10} marginBottom={1}>
          <Input
            placeholder="Website"
            name="website"
            type="text"
            value={values.website}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.website && touched.website && 'error'}
          />
          <FeedbackError name="website" errors={errors} touched={touched} />
        </Grid>
        <Grid item xs={12} sm={10} marginBottom={1}>
          <Grid container justifyContent="space-between">
            <Grid item xs={12} sm={3} marginBottom={1}>
              <Input
                placeholder="Business Type"
                name="business_type"
                type="text"
                value={values.business_type}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.business_type && touched.business_type && 'error'}
              />
              <FeedbackError name="business_type" errors={errors} touched={touched} />
            </Grid>
            <Grid item xs={12} sm={3} marginBottom={1}>
              <PhoneInput
                country="us"
                value={values.business_number}
                onChange={(phone) => {
                  setFieldValue('business_number', phone);
                }}
                onBlur={handleBlur}
                onFocus={onFocus}
                onlyCountries={['us']}
                placeholder="Phone Number"
                disableCountryCode="true"
                inputClass="phone-class"
                containerClass="container-class"
                // react-phone-input-2 only forwards attributes to the real
                // <input> via inputProps, so set name here for scroll-to-error
                // targeting (the top-level name prop never reaches the DOM).
                inputProps={{ name: 'business_number' }}
                isValid={!(touched.business_number && Boolean(errors.business_number))}
              />
              {/* <FeedbackError name="business_number" errors={errors} touched={touched} /> */}
              {errors.business_number && (
                <div className="input-feedback">{errors.business_number}</div>
              )}
            </Grid>
            <Grid item xs={12} sm={3} marginBottom={1}>
              <Input
                placeholder="EIN (9 digits, e.g. 12-3456789)"
                name="business_EIN"
                type="text"
                value={values.business_EIN}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.business_EIN && touched.business_EIN && 'error'}
              />
              <FeedbackError name="business_EIN" errors={errors} touched={touched} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container justifyContent={justifyContentValue} marginBottom={2}>
        <Grid item xs={10} sm={8} md={7} lg={8.5}>
          <Textarea
            type="text"
            name="description"
            placeholder="Venue description"
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength="1500"
            className={errors.description && touched.description && 'error'}
          />
          <FeedbackError name="description" errors={errors} touched={touched} />
        </Grid>
      </Grid>
      <Grid container justifyContent="center" marginBottom={1}>
        <Typography textAlign="center" variant="h6" fontWeight="bold">
          Point of Contact
        </Typography>
      </Grid>
      <Grid
        container
        xs={12}
        justifyContent={justifyContentValue}
        marginBottom={3}
        paddingLeft={spacingValue(5)}>
        <ContactPoint
          values={values}
          handleChange={handleChange}
          handleBlur={handleBlur}
          touched={touched}
          errors={errors}
        />
      </Grid>
      <Grid container justifyContent="center" marginBottom={1}>
        <Typography textAlign="center" variant="h6" fontWeight="bold">
          Social Networks
        </Typography>
      </Grid>
      <Grid container justifyContent={justifyContentValue} paddingLeft={spacingValue(5)}>
        <SocialNetworks
          values={values}
          handleChange={handleChange}
          handleBlur={handleBlur}
          touched={touched}
          errors={errors}
        />
      </Grid>
      {canManageVenue() && (
        <>
          <Grid container justifyContent="center" marginBottom={1}>
            <Typography textAlign="center" variant="h6" fontWeight="bold">
              Pictures uploaded previously
            </Typography>
          </Grid>
          <Grid container justifyContent={justifyContentValue} paddingLeft={spacingValue(5)}>
            {picturesList != null ? (
              <PicturesVenue
                images={picturesList}
                onSelectImage={(bannerKey) => handleImageSelect(bannerKey, setFieldValue)}
                onDeleteImage={(image) => handleDeleteImage(image, setFieldValue)}
                currentBannerKey={values.banner || currentVenue?.banner}
              />
            ) : null}
          </Grid>
        </>
      )}

      <Grid container justifyContent="center" marginBottom={1}>
        <Typography textAlign="center" variant="h6" fontWeight="bold">
          Pictures
        </Typography>
      </Grid>
      <Grid container justifyContent={justifyContentValue}>
        <Grid item xs={10} sm={8.5}>
          <DropzoneFile files={venuePictures} onChangeFiles={onChangeFiles} />
        </Grid>
      </Grid>
      <Grid container justifyContent={justifyContentValue} marginLeft={spacingValue(5)}>
        <Grid container justifyContent={justifyContentValue}>
          <Grid item sm={9}>
            <Dropdown hidden={false} title="Venue Features">
              <VenueFeaturesCheck
                features={featureList}
                onChangeState={onChangeStatusFeatureList}
              />
            </Dropdown>
          </Grid>
        </Grid>
        <Grid container justifyContent="center">
          <Grid item sm={9}>
            {/** <Dropdown title="Term and Services">-</Dropdown>* */}
          </Grid>
        </Grid>
      </Grid>

      {submitCount > 0 && errorCount > 0 && (
        <Grid container justifyContent="center" marginTop={2}>
          <Typography color="error" variant="body2" fontWeight="bold">
            {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} attention above before
            this can be submitted.
          </Typography>
        </Grid>
      )}
      <Grid container justifyContent="center" px={spacingValue(10)} marginTop={3}>
        <Grid item xs={12} sm={7}>
          <Button type="submit" label={canManageVenue() ? 'Save' : 'Add Venue'} />
        </Grid>
      </Grid>
    </Form>
  );
}

/* eslint-disable react/forbid-prop-types */
AddVenueFormBody.propTypes = {
  values: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  submitCount: PropTypes.number.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  states: PropTypes.array.isRequired,
  cities: PropTypes.array.isRequired,
  setSelectedState: PropTypes.func.isRequired,
  justifyContentValue: PropTypes.string.isRequired,
  spacingValue: PropTypes.func.isRequired,
  canManageVenue: PropTypes.func.isRequired,
  picturesList: PropTypes.array,
  handleImageSelect: PropTypes.func.isRequired,
  handleDeleteImage: PropTypes.func.isRequired,
  currentVenue: PropTypes.object,
  venuePictures: PropTypes.array,
  onChangeFiles: PropTypes.func.isRequired,
  featureList: PropTypes.array,
  onChangeStatusFeatureList: PropTypes.func.isRequired,
};
/* eslint-enable react/forbid-prop-types */

AddVenueFormBody.defaultProps = {
  onFocus: undefined,
  picturesList: [],
  currentVenue: null,
  venuePictures: [],
  featureList: [],
};

export default function AddVenue({ onSaveVenue }) {
  return (
    <Grid container marginTop={5} justifyContent="center">
      <TranslucentContainer>
        <FormContentVenue onSaveVenue={onSaveVenue} />
      </TranslucentContainer>
    </Grid>
  );
}

FormContentVenue.propTypes = {
  onSaveVenue: PropTypes.func.isRequired,
};

AddVenue.propTypes = {
  onSaveVenue: PropTypes.func.isRequired,
};
